import { ReadonlyArray } from "@morphism/fp";

import React from "react";
import styled from "styled-components";

import { Button } from "~/ButtonV2";
import * as Data from "~/Data";
import * as GenericDriver from "~/Driver";
import * as Log from "~/Log";
import { Select as GenericSelect } from "~/SelectV2";

export namespace Driver {
  export const column = () => ({
    title: "Driver",
    dataIndex: "driver",
    filterDropdown,
    onFilter,
    sorter,
    render,
  });

  const sorter = (a: Log.Summary, b: Log.Summary) =>
    GenericDriver.compare(a.summary.driver, b.summary.driver);

  const render = (_: unknown, { summary }: Log.Summary) => (
    <GenericDriver.Name.Component {...summary.driver} />
  );

  const filterDropdown = (props: Filter.Props) => <Filter {...props} />;

  const onFilter = (
    value: string | number | boolean,
    { summary }: Log.Summary
  ) => summary.driver.id === `${value}`;

  function Filter({
    clearFilters,
    confirm,
    selectedKeys,
    setSelectedKeys,
  }: Filter.Props) {
    return (
      <>
        <Filter.Controls>
          <Button
            type="link"
            size="small"
            disabled={!selectedKeys[0]}
            onClick={clearFilters}
          >
            Reset
          </Button>
          <Button type="primary" size="small" onClick={confirm}>
            OK
          </Button>
        </Filter.Controls>
        <Filter.Tags>
          <Filter.Select
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
          />
        </Filter.Tags>
      </>
    );
  }

  namespace Filter {
    export type Props = {
      readonly confirm: () => void;
      readonly clearFilters?: () => void;
      readonly selectedKeys: readonly string[];
      readonly setSelectedKeys: (keys: readonly string[]) => void;
    };

    export const Controls = styled.div`
      display: flex;
      justify-content: space-between;
      padding: 8px;
      margin-bottom: 4px;
      border-bottom: 1px solid #f0f0f0;
    `;

    export const Tags = styled.div`
      padding: 0 4px 4px 4px;
    `;

    export const Select = ({
      selectedKeys,
      setSelectedKeys,
    }: Pick<Props, "selectedKeys" | "setSelectedKeys">) => {
      const drivers = pipe(
        GenericDriver.useStore(),
        Data.Store.toCache,
        Data.Cache.toList((id, driver) => ({ ...driver, id })),
        ReadonlyArray.sort(GenericDriver.order())
      );

      return (
        <GenericSelect
          placeholder="Filter by drivers"
          mode="multiple"
          bordered={false}
          onChange={
            ((_value: never, selected: readonly { readonly key: string }[]) =>
              setSelectedKeys(selected.map(({ key }) => key))) as never
          }
          options={drivers}
          selectedKeys={selectedKeys}
          optionToKey={({ id }) => id}
          optionToValue={GenericDriver.toString}
          Option={(driver) => (
            <GenericDriver.Name.Component noLink {...driver} />
          )}
        />
      );
    };
  }
}
